import React, { useCallback, useEffect, useState } from "react";
import { Link, redirect, useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "src/store/hooks";
import { toast } from "react-toastify";
import {
  CREDIT_STATUS_COLOR,
  CREDIT_STATUS_LABEL,
  CREDIT_TYPE_LABEL,
  CreditStatus,
  CreditType,
  PARTNER_TYPE_LABEL,
  PartnerType,
  fetchCreditNoteDetail,
  resetCreditNoteView,
  undoCreditUsage,
  updateCreditNote,
} from "src/store/reducers/creditNotes";
import BookingDetailItem from "../booking/BookingDetailItem";
import ConfirmationModal from "src/layouts/modals/ConfirmationModal";
import { FormikAPIErrorHandler } from "src/utils/errorUtil";
import CreditNoteFormCanvas from "./CreditNoteFormCanvas";
import UseCreditModal from "./UseCreditModal";

const ViewCreditNote = () => {
  const dispatch = useAppDispatch();
  const { id: creditNoteId } = useParams<{ id: string }>();

  const viewStatus = useAppSelector((state) => state.creditNotes.view.status);
  const noteData = useAppSelector((state) => state.creditNotes.view.data);

  const [showEdit, setShowEdit] = useState(false);
  const [showUseModal, setShowUseModal] = useState(false);
  const [showUsages, setShowUsages] = useState(true);
  const [undoUsageId, setUndoUsageId] = useState<string>();
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  useEffect(() => {
    if (!creditNoteId) redirect("/credit-notes");
  }, [creditNoteId]);

  const retry = useCallback(() => {
    if (creditNoteId) dispatch(fetchCreditNoteDetail(creditNoteId));
  }, [creditNoteId, dispatch]);

  useEffect(() => {
    if (viewStatus === "idle") retry();
  }, [viewStatus, retry]);

  useEffect(() => {
    if (noteData?.id !== creditNoteId) {
      dispatch(resetCreditNoteView());
    }
  }, [noteData?.id, creditNoteId, dispatch]);

  const handleCancel = useCallback(async () => {
    if (!noteData) return;
    try {
      await dispatch(
        updateCreditNote({ id: noteData.id, status: 5 })
      ).unwrap();
      toast.success("Credit note cancelled");
      setShowCancelConfirm(false);
      dispatch(resetCreditNoteView());
    } catch (e: any) {
      FormikAPIErrorHandler(e.message, undefined, toast.error);
    }
  }, [dispatch, noteData]);

  const handleUndoUsage = useCallback(async () => {
    if (!undoUsageId) return;
    try {
      await dispatch(undoCreditUsage(undoUsageId)).unwrap();
      toast.success("Usage undone successfully");
      setUndoUsageId(undefined);
    } catch (e: any) {
      FormikAPIErrorHandler(e.message, undefined, toast.error);
      setUndoUsageId(undefined);
    }
  }, [dispatch, undoUsageId]);

  const isEditable =
    noteData?.status !== CreditStatus.Cancelled &&
    noteData?.status !== CreditStatus.Expired &&
    noteData?.status !== CreditStatus.FullyUsed;

  const isUsable =
    noteData?.status === CreditStatus.Active ||
    noteData?.status === CreditStatus.PartiallyUsed;

  const remaining = noteData
    ? parseFloat(noteData.amount) - parseFloat(noteData.used_amount)
    : 0;

  return (
    <>
      <div className="container-fluid mh-auto">
        <div className="row">
          <div className="col-lg-12">
            <div className="card">
              <div className="card-body">
                {viewStatus === "success" && noteData ? (
                  <>
                    {/* Header row */}
                    <div className="d-flex justify-content-between align-items-start mb-4">
                      <div>
                        <h4 className="mb-1">{noteData.reference_number}</h4>
                        <span
                          className={`badge light badge-${
                            CREDIT_STATUS_COLOR[noteData.status]
                          } me-2`}
                        >
                          {CREDIT_STATUS_LABEL[noteData.status]}
                        </span>
                        <span
                          className={`badge badge-outline-${
                            noteData.credit_type === CreditType.Given
                              ? "primary"
                              : "info"
                          }`}
                        >
                          {CREDIT_TYPE_LABEL[noteData.credit_type]}
                        </span>
                      </div>
                      <div className="d-flex gap-2">
                        {isUsable && (
                          <button
                            className="btn btn-success btn-sm"
                            onClick={() => setShowUseModal(true)}
                          >
                            <i className="fas fa-check-circle me-1"></i>
                            Use Credit
                          </button>
                        )}
                        {isEditable && (
                          <button
                            className="btn btn-primary btn-sm"
                            onClick={() => setShowEdit(true)}
                          >
                            <i className="fa-solid fa-pen-to-square me-1"></i>
                            Edit
                          </button>
                        )}
                        {noteData.status !== CreditStatus.Cancelled &&
                          noteData.status !== CreditStatus.Expired && (
                            <button
                              className="btn btn-danger btn-sm"
                              onClick={() => setShowCancelConfirm(true)}
                            >
                              Cancel Note
                            </button>
                          )}
                      </div>
                    </div>

                    {/* Amount summary */}
                    <div className="row mb-4">
                      <div className="col-md-3">
                        <div className="card border text-center p-3">
                          <small className="text-muted">Total Amount</small>
                          <h5 className="mb-0">
                            {noteData.currency}{" "}
                            {parseFloat(noteData.amount).toLocaleString(
                              "en-IN"
                            )}
                          </h5>
                        </div>
                      </div>
                      <div className="col-md-3">
                        <div className="card border text-center p-3">
                          <small className="text-muted">Used Amount</small>
                          <h5 className="mb-0 text-warning">
                            {parseFloat(noteData.used_amount).toLocaleString(
                              "en-IN"
                            )}
                          </h5>
                        </div>
                      </div>
                      <div className="col-md-3">
                        <div className="card border text-center p-3">
                          <small className="text-muted">Remaining</small>
                          <h5 className="mb-0 text-success">
                            {remaining.toLocaleString("en-IN")}
                          </h5>
                        </div>
                      </div>
                      <div className="col-md-3">
                        <div className="card border text-center p-3">
                          <small className="text-muted">Expiry</small>
                          <h5 className="mb-0">
                            {noteData.expiry_date
                              ? new Date(
                                  noteData.expiry_date
                                ).toLocaleDateString("en-IN")
                              : "No expiry"}
                          </h5>
                        </div>
                      </div>
                    </div>

                    {/* Details */}
                    <div className="row">
                      <div className="col-xl-6">
                        <ul className="list-unstyled">
                          {noteData.credit_type === CreditType.Given ? (
                            <>
                              <BookingDetailItem
                                title="Customer Name"
                                value={noteData.customer_name || "—"}
                              />
                              <BookingDetailItem
                                title="Customer Phone"
                                value={
                                  noteData.customer_number
                                    ? String(noteData.customer_number)
                                    : "—"
                                }
                              />
                              <BookingDetailItem
                                title="Customer Email"
                                value={noteData.customer_email || "—"}
                              />
                            </>
                          ) : (
                            <>
                              <BookingDetailItem
                                title="Partner Type"
                                value={
                                  noteData.partner_type
                                    ? PARTNER_TYPE_LABEL[
                                        noteData.partner_type as PartnerType
                                      ]
                                    : "—"
                                }
                              />
                              <BookingDetailItem
                                title="Partner Name"
                                value={noteData.partner_name || "—"}
                              />
                              <BookingDetailItem
                                title="Partner City"
                                value={noteData.partner_city || "—"}
                              />
                            </>
                          )}
                          <BookingDetailItem
                            title="Reason"
                            value={noteData.reason || "—"}
                          />
                          <BookingDetailItem
                            title="Notes"
                            value={noteData.notes || "—"}
                          />
                        </ul>
                      </div>
                      <div className="col-xl-6">
                        <ul className="list-unstyled">
                          {noteData.booking && (
                            <BookingDetailItem
                              title="Linked Booking"
                              value={
                                typeof noteData.booking === "object"
                                  ? (noteData.booking as any).customer_name ||
                                    (noteData.booking as any).id
                                  : noteData.booking
                              }
                            />
                          )}
                          <BookingDetailItem
                            title="Created"
                            value={new Date(
                              noteData.created_date
                            ).toLocaleString("en-IN")}
                          />
                          <BookingDetailItem
                            title="Updated"
                            value={new Date(
                              noteData.updated_date
                            ).toLocaleString("en-IN")}
                          />
                        </ul>
                      </div>
                    </div>
                  </>
                ) : viewStatus === "pending" || viewStatus === "idle" ? (
                  <div className="d-flex align-items-center">
                    <strong>Loading...</strong>
                    <div
                      className="spinner-border ms-auto"
                      role="status"
                      aria-hidden="true"
                    ></div>
                  </div>
                ) : (
                  <div className="d-flex align-items-center">
                    <button
                      type="button"
                      className="btn btn-warning"
                      onClick={retry}
                    >
                      Retry
                    </button>
                    &nbsp;
                    <strong>Please try again</strong>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Usages section */}
      {viewStatus === "success" && noteData && (
        <div className="container-fluid">
          <div className="row">
            <div className="col-xl-12">
              <div className="card h-auto">
                <div className="card-header">
                  <h4
                    className="heading mb-0"
                    role="button"
                    onClick={() => setShowUsages((s) => !s)}
                  >
                    <span className="btn-icon-start">
                      <i
                        className={`fa ${showUsages ? "fa-minus" : "fa-plus"} color-info`}
                      ></i>
                    </span>
                    Usage History ({noteData.usages?.length || 0})
                  </h4>
                  {isUsable && (
                    <button
                      className="btn btn-success btn-sm"
                      onClick={() => setShowUseModal(true)}
                    >
                      + Record Usage
                    </button>
                  )}
                </div>
                <div className={showUsages ? "card-body" : "card-body d-none"}>
                  {noteData.usages && noteData.usages.length > 0 ? (
                    <div className="table-responsive active-projects">
                      <div className="dataTables_wrapper no-footer">
                        <table className="table table-hover dataTable no-footer mb-0">
                          <thead>
                            <tr>
                              <th>Amount Used</th>
                              <th>Booking</th>
                              <th>Recorded By</th>
                              <th>Notes</th>
                              <th>Date</th>
                              <th>Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {noteData.usages.map((usage) => {
                              const canUndo =
                                noteData.status !== CreditStatus.Expired &&
                                noteData.status !== CreditStatus.Cancelled;
                              return (
                                <tr key={usage.id}>
                                  <td>
                                    <strong>
                                      {noteData.currency}{" "}
                                      {parseFloat(
                                        usage.amount_used
                                      ).toLocaleString("en-IN")}
                                    </strong>
                                  </td>
                                  <td>
                                    {usage.booking ? (
                                      <Link
                                        to={`/booking-view/${usage.booking.id}`}
                                        target="_blank"
                                        className="text-primary"
                                      >
                                        {usage.booking.customer_name}
                                      </Link>
                                    ) : (
                                      "—"
                                    )}
                                  </td>
                                  <td>{usage.user?.name || "—"}</td>
                                  <td>{usage.notes || "—"}</td>
                                  <td>
                                    {new Date(
                                      usage.created_date
                                    ).toLocaleString("en-IN")}
                                  </td>
                                  <td>
                                    {canUndo && (
                                      <button
                                        className="btn btn-warning shadow btn-xs sharp"
                                        title="Undo usage"
                                        onClick={() =>
                                          setUndoUsageId(usage.id)
                                        }
                                      >
                                        <i className="fas fa-undo" />
                                      </button>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ) : (
                    <div className="d-flex align-items-center">
                      <strong>No usages recorded yet</strong>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modals */}
      {noteData && (
        <>
          <UseCreditModal
            show={showUseModal}
            hide={() => setShowUseModal(false)}
            creditNoteId={noteData.id}
            remainingAmount={remaining}
          />
          <CreditNoteFormCanvas
            show={showEdit}
            hide={() => setShowEdit(false)}
            editData={
              noteData
                ? {
                    ...noteData,
                    booking:
                      typeof noteData.booking === "object" && noteData.booking
                        ? (noteData.booking as any).id
                        : (noteData.booking as string | null),
                  }
                : undefined
            }
            Title="Edit Credit Note"
          />
        </>
      )}

      <ConfirmationModal
        show={typeof undoUsageId !== "undefined"}
        handleClose={() => setUndoUsageId(undefined)}
        confirmClose={handleUndoUsage}
      />
      <ConfirmationModal
        show={showCancelConfirm}
        handleClose={() => setShowCancelConfirm(false)}
        confirmClose={handleCancel}
      />
    </>
  );
};

export default ViewCreditNote;
